/**
 * 计费重构（2026-06-28）— 充值弹窗。
 *
 * 受控组件：调用方持有 open/onClose。预付费钱包模型下，这是用户往
 * 余额里加钱的唯一入口（PaywallModal 内部也复用它）。
 *
 * 预设 $5/$10/$20/$50/$100（默认 $20）+ 自定义整数金额（$5–$500）。
 * 确认走 `api.createTopupSession(cents)` 拿 Stripe hosted URL 后整页
 * 跳转。Stripe 未配置时后端 500 + "not configured"，这里转成
 * 「充值暂未开放」的友好提示而非抛通用错误。
 *
 * 视觉与 PaywallModal 一致：暖纸色 overlay + card、mono kicker、serif
 * 标题、primary/ghost 按钮。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';

const PRESETS = [5, 10, 20, 50, 100] as const;
const DEFAULT_AMOUNT = 20;
const MIN_AMOUNT = 5;
const MAX_AMOUNT = 500;

export function TopupModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // The amount actually charged. Starts on the default preset; clicking a
  // preset sets it directly, typing in the custom box overrides it.
  const [amount, setAmount] = useState<number>(DEFAULT_AMOUNT);
  // Raw text of the custom-amount input. Empty when the user is driving via
  // presets — we only parse/validate it once they type something in.
  const [custom, setCustom] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  // Custom input wins when non-empty. Parse as an integer; NaN / out-of-range
  // both disable the confirm button (and surface the range hint).
  const customTrimmed = custom.trim();
  const usingCustom = customTrimmed.length > 0;
  const customNum = usingCustom ? Number(customTrimmed) : NaN;
  const customValid =
    usingCustom &&
    Number.isInteger(customNum) &&
    customNum >= MIN_AMOUNT &&
    customNum <= MAX_AMOUNT;
  const customInvalid = usingCustom && !customValid;

  const effectiveAmount = usingCustom ? (customValid ? customNum : NaN) : amount;
  const canConfirm =
    !submitting &&
    Number.isInteger(effectiveAmount) &&
    effectiveAmount >= MIN_AMOUNT &&
    effectiveAmount <= MAX_AMOUNT;

  function pickPreset(value: number) {
    setAmount(value);
    setCustom('');
    setError(null);
  }

  function onCustomChange(raw: string) {
    // Keep only the digits the user typed; an empty field falls back to the
    // last-selected preset for the confirm amount.
    setCustom(raw.replace(/[^\d]/g, ''));
    setError(null);
  }

  async function onConfirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    try {
      const { url } = await api.createTopupSession(effectiveAmount * 100);
      window.location.assign(url);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // The server replies 500 + "billing not configured" when Stripe keys
      // are absent — show the early-access copy instead of the raw error.
      setError(
        msg.includes('not configured')
          ? t('billing.topupNotConfigured')
          : msg,
      );
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26, 24, 20, 0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1100,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--paper-0)',
          border: '1px solid var(--ink-0)',
          borderRadius: 6,
          maxWidth: 460,
          width: '100%',
          padding: '28px 28px 24px',
          boxShadow: '6px 6px 0 0 rgba(26,24,20,0.12)',
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            letterSpacing: '.16em',
            fontWeight: 700,
            color: 'var(--amber)',
            marginBottom: 12,
          }}
        >
          {t('billing.topUp').toUpperCase()}
        </div>

        <h2
          className="serif"
          style={{
            fontSize: 26,
            fontWeight: 500,
            lineHeight: 1.15,
            margin: '0 0 14px',
            color: 'var(--ink-0)',
            letterSpacing: '-.01em',
          }}
        >
          {t('billing.topUpTitle')}
        </h2>

        <p
          style={{
            fontSize: 14.5,
            lineHeight: 1.6,
            color: 'var(--ink-1)',
            margin: '0 0 20px',
          }}
        >
          {t('billing.prepaidExplainer')}
        </p>

        {/* Preset amount grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: 8,
            marginBottom: 16,
          }}
        >
          {PRESETS.map((value) => {
            const selected = !usingCustom && amount === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => pickPreset(value)}
                className="mono"
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '.02em',
                  padding: '12px 0',
                  borderRadius: 4,
                  cursor: 'pointer',
                  border: selected
                    ? '1px solid var(--ink-0)'
                    : '1px solid var(--rule-s)',
                  background: selected ? 'var(--ink-0)' : 'transparent',
                  color: selected ? 'var(--paper-0)' : 'var(--ink-1)',
                }}
              >
                ${value}
              </button>
            );
          })}
        </div>

        {/* Custom amount */}
        <label
          className="mono"
          style={{
            display: 'block',
            fontSize: 10,
            letterSpacing: '.14em',
            fontWeight: 700,
            color: 'var(--ink-2)',
            marginBottom: 6,
          }}
        >
          {t('billing.customAmount').toUpperCase()}
        </label>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            border: customInvalid
              ? '1px solid var(--red, #d23f3f)'
              : '1px solid var(--rule-s)',
            borderRadius: 4,
            padding: '0 12px',
            marginBottom: customInvalid ? 6 : 18,
          }}
        >
          <span
            className="serif"
            style={{ fontSize: 16, color: 'var(--ink-2)', marginRight: 4 }}
          >
            $
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={custom}
            onChange={(e) => onCustomChange(e.target.value)}
            placeholder={`${MIN_AMOUNT}–${MAX_AMOUNT}`}
            className="serif"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 16,
              color: 'var(--ink-0)',
              padding: '11px 0',
            }}
          />
        </div>
        {customInvalid && (
          <p
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: '.02em',
              color: 'var(--red, #d23f3f)',
              margin: '0 0 18px',
            }}
          >
            {t('billing.topupRange')}
          </p>
        )}

        {error && (
          <p
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: 'var(--red, #d23f3f)',
              margin: '0 0 16px',
            }}
          >
            {error}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: '.12em',
              fontWeight: 700,
              padding: '10px 16px',
              border: '1px solid var(--rule-s)',
              background: 'transparent',
              color: 'var(--ink-2)',
              cursor: 'pointer',
            }}
          >
            {t('billing.notNow')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: '.12em',
              fontWeight: 700,
              padding: '10px 16px',
              border: '1px solid var(--ink-0)',
              background: 'var(--ink-0)',
              color: 'var(--paper-0)',
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: canConfirm ? 1 : 0.45,
            }}
          >
            {submitting
              ? '…'
              : t('billing.confirmTopup', {
                  amount: Number.isInteger(effectiveAmount)
                    ? effectiveAmount
                    : MIN_AMOUNT,
                })}
          </button>
        </div>
      </div>
    </div>
  );
}
